/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import cx from 'classnames';
import * as React from 'react';
import { useTheme } from '../utils/index.js';
import { HeaderSplitButton } from './HeaderSplitButton.js';
import { HeaderDropdownButton } from './HeaderDropdownButton.js';
import { HeaderBasicButton } from './HeaderBasicButton.js';
const isSplitButton = (props) => {
    return !!props.menuItems && !!props.onClick;
};
const isDropdownButton = (props) => {
    return !!props.menuItems;
};
/**
 * Header button that handles slim state of the `Header` it's in.
 * When in slim mode, will only display the name and reduce icon size.
 * Will display an arrow on the right if `menuItems` prop is provided.
 * @example
 * <HeaderButton name='Project A' description='0n00434' menuItems={...} />
 * <HeaderButton name='Project B' description='0n00434' startIcon={<SvgImodelHollow />} />
 * <HeaderButton name='Project C' startIcon={<img style={{ objectFit: 'cover' }} src='project.png' />} />
 * <HeaderButton name='Project D' isActive />
 */
export const HeaderButton = React.forwardRef((props, ref) => {
    const { name, description, isActive = false, className, startIcon, menuItems, disabled, ...rest } = props;
    useTheme();
    const buttonProps = {
        startIcon: React.isValidElement(startIcon)
            ? React.cloneElement(startIcon, {
                className: cx('iui-header-breadcrumb-button-icon', startIcon.props.className),
            })
            : undefined,
        className: className,
        children: (React.createElement("span", { className: 'iui-header-breadcrumb-button-text' },
            React.createElement("span", { className: 'iui-header-breadcrumb-button-text-label' }, name),
            description && (React.createElement("span", { className: 'iui-header-breadcrumb-button-text-sublabel' }, description)))),
        ref: ref,
        disabled: disabled,
        ...(!!menuItems && { menuItems }),
        ...rest,
    };
    const headerButton = isSplitButton(buttonProps) ? (React.createElement(HeaderSplitButton, { ...buttonProps })) : isDropdownButton(buttonProps) ? (React.createElement(HeaderDropdownButton, { ...buttonProps })) : (React.createElement(HeaderBasicButton, { ...buttonProps }));
    return (React.createElement("li", { className: 'iui-header-breadcrumb-item', "aria-current": isActive ? 'location' : undefined, "aria-disabled": disabled ? 'true' : undefined }, headerButton));
});
export default HeaderButton;
